<template>
  <View direction="column" gap="lg">
    <View justify="space-between" align="center" gap="md" wrap="wrap">
      <View direction="column" gap="xs">
        <Text variant="h2">Team</Text>
        <Text variant="body-large" color="secondary">{{ subtitle }}</Text>
      </View>

      <Button v-if="isAdmin" size="medium" data-testid="team-invite-button" @click="inviteModalRef?.open()">
        <Icon name="user-plus-01" size="small" />
        <span>Invite</span>
      </Button>
    </View>

    <View v-if="state.status === 'pending'" justify="center" padding="lg">
      <LoadingIndicator />
    </View>

    <EmptyState
      v-else-if="state.status === 'error'"
      icon="alert-triangle"
      color="red"
      title="Couldn't load your team"
      description="Please try again."
    >
      <template #action>
        <Button variant="outline" size="medium" @click="refresh()">Retry</Button>
      </template>
    </EmptyState>

    <template v-else>
      <View direction="column" gap="sm">
        <TeamPersonRow
          v-for="member in roster"
          :key="member.investorId"
          :name="nameOf(member)"
          :details="member.email"
          :meta="holdingsLine(member)"
          :is-you="member.isYou"
          :badges="badgesOf(member)"
        >
          <template v-if="member.isYou && avatar" #leading>
            <Avatar :user-name="nameOf(member)" :image-url="avatar" size="medium" />
          </template>

          <template v-if="isAdmin && !member.isYou" #action>
            <MenuButton>
              <template #menu-button-trigger>
                <!-- MenuButton's trigger is the tab stop and the button; the icon button inside only draws and names it -->
                <IconButton
                  icon="dots-horizontal"
                  variant="ghost"
                  shape="square"
                  size="small"
                  tabindex="-1"
                  :aria-label="`${nameOf(member)} actions`"
                />
              </template>
              <MenuButtonItem
                v-if="member.role === CompanyRole.MEMBER && portfolios.length"
                title="Add to portfolios"
                icon="folder"
                @click="addToPortfoliosModalRef?.open(member)"
              />
              <MenuButtonItem
                :title="member.role === CompanyRole.ADMIN ? 'Make member' : 'Make admin'"
                icon="user-check-01"
                @click="changeRoleModalRef?.open(member)"
              />
              <Divider role="separator" />
              <MenuButtonItem
                title="Remove from team"
                icon="trash"
                variant="dangerous"
                @click="removeMemberModalRef?.open(member)"
              />
            </MenuButton>
          </template>
        </TeamPersonRow>
      </View>

      <EmptyState
        v-if="isTeamOfOne"
        icon="users-01"
        color="orange"
        title="Invite your teammates"
        description="They'll see every property. You hand them properties from a property's Team tab or through a portfolio."
      >
        <template #action>
          <Button variant="outline" size="medium" @click="inviteModalRef?.open()">Invite a teammate</Button>
        </template>
      </EmptyState>

      <Hint v-if="!isAdmin && whoToAsk" icon="info-circle">
        Only admins invite people and change roles. Ask {{ whoToAsk }} to do it for you.
      </Hint>

      <template v-if="isAdmin && pendingInvites.length">
        <Divider />

        <View direction="column" gap="xs">
          <Text variant="h5">Pending invites</Text>
          <Text variant="body-medium" color="secondary">
            Resending emails a fresh link; the one sent before stops working.
          </Text>
        </View>

        <View direction="column" gap="sm">
          <PendingInviteRow
            v-for="invite in pendingInvites"
            :key="invite.id"
            :invite="invite"
            @revoke="revokeInviteModalRef?.open(invite)"
          />
        </View>
      </template>
    </template>

    <InviteMemberModal ref="inviteModalRef" />
    <AddToPortfoliosModal ref="addToPortfoliosModalRef" />
    <ChangeRoleModal ref="changeRoleModalRef" />
    <RemoveMemberModal ref="removeMemberModalRef" />
    <RevokeInviteModal ref="revokeInviteModalRef" />
  </View>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, ref, watch } from 'vue';

import {
  Avatar,
  Button,
  Divider,
  EmptyState,
  Hint,
  Icon,
  IconButton,
  LoadingIndicator,
  MenuButton,
  MenuButtonItem,
  Text,
  View,
} from '@tallo/design-system';
import {
  CompanyRole,
  CompanyTeamMemberDto,
  companyRoleBadgeVariants,
  companyRoleLabels,
  seatHoldings,
  teamMemberName as nameOf,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
} from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { TeamPersonRow, TeamRoleBadge, usePortfolioQuery } from '@tallo/investor/property';

import AddToPortfoliosModal from '../team/add-to-portfolios-modal.vue';
import ChangeRoleModal from '../team/change-role-modal.vue';
import InviteMemberModal from '../team/invite-member-modal.vue';
import PendingInviteRow from '../team/pending-invite-row.vue';
import RemoveMemberModal from '../team/remove-member-modal.vue';
import RevokeInviteModal from '../team/revoke-invite-modal.vue';

const investorStore = useInvestorStore();
const { investor, isAdmin } = storeToRefs(investorStore);
const { state, members, pendingInvites, refresh } = useCompanyTeamQuery();
const { properties, data: propertiesPage } = usePortfolioQuery();
const { portfolios } = useCompanyPortfoliosQuery();

const inviteModalRef = ref<InstanceType<typeof InviteMemberModal>>();
const addToPortfoliosModalRef = ref<InstanceType<typeof AddToPortfoliosModal>>();
const changeRoleModalRef = ref<InstanceType<typeof ChangeRoleModal>>();
const removeMemberModalRef = ref<InstanceType<typeof RemoveMemberModal>>();
const revokeInviteModalRef = ref<InstanceType<typeof RevokeInviteModal>>();

const avatar = computed(() => investor.value?.user?.avatar);

// You first; the rest keep the server's order, by name
const roster = computed(() => [...members.value].sort((a, b) => Number(b.isYou) - Number(a.isYou)));

const subtitle = computed(() => `${members.value.length} ${pluralize('member', members.value.length)}`);

// The state every existing account starts in: the admin alone, nobody invited yet
const isTeamOfOne = computed(() => isAdmin.value && members.value.length === 1 && pendingInvites.value.length === 0);

// A single admin is named; with several, any of them will do, so the note stays short
const whoToAsk = computed(() => {
  const admins = members.value.filter((member) => member.role === CompanyRole.ADMIN);

  if (admins.length === 1) return nameOf(admins[0]);

  return admins.length ? 'an admin' : '';
});

// Home sends a member with nothing to work to an admin, so the admins' roster shows who is waiting
function badgesOf(member: CompanyTeamMemberDto): TeamRoleBadge[] {
  const role: TeamRoleBadge = { label: companyRoleLabels[member.role], variant: companyRoleBadgeVariants[member.role] };

  return isAdmin.value && member.nothingToWork ? [role, { label: 'No properties yet', variant: 'yellow' }] : [role];
}

function holdingsLine(member: CompanyTeamMemberDto): string | undefined {
  const holdings = seatHoldings(
    member.investorId,
    properties.value,
    portfolios.value,
    (propertiesPage.value?.totalPages ?? 1) > 1,
  );
  const parts = [
    holdings.managedUnitCount
      ? `Manages ${holdings.isPartial ? 'at least ' : ''}${pluralize('property', holdings.managedUnitCount, true)}`
      : '',
    holdings.ownedPortfolios.length ? `owns ${pluralize('portfolio', holdings.ownedPortfolios.length, true)}` : '',
  ].filter(Boolean);

  if (!parts.length) return undefined;

  const line = parts.join(' · ');

  return line.charAt(0).toUpperCase() + line.slice(1);
}

// The roster is read fresh, the seat at sign-in: when they disagree about your role, the seat is
// stale and this page would offer or hide the admin controls wrongly
watch(members, (list) => {
  const me = list.find((member) => member.isYou);

  if (me && investor.value && me.role !== investor.value.role) {
    investorStore.load();
  }
});
</script>
